import type { Initiative } from '../../api/initiative-client/initiative-client.type';
import i18n from '../../base-dictionary';
import { formatDate, formatMonthDate } from '../../helpers/date-time-helpers';
import { Metric } from './strategy-v2.type';

/**
 * Formats an initiative date string to a human-readable format
 *
 * @param {string | undefined | null} date - The date string to format
 * @returns {string} The formatted date string
 */
const formatInitiativeDate = (date: string | undefined | null): string => {
  return formatDate(date, { fallback: i18n.t('strategy.initiatives.no_date', '-') });
};

/**
 * Returns a human-readable label for a given metric
 *
 * @param {Metric} metric - The metric to get a label for
 * @returns {string} The human-readable label for the metric
 */
const getMetricLabel = (metric: Metric): string => {
  switch (metric) {
    case Metric.Tasks:
      return i18n.t('strategy.initiatives.metrics.tasks');
    case Metric.Points:
      return i18n.t('strategy.initiatives.metrics.points');
    default:
      return metric;
  }
};

/**
 * Formats a date string to a human-readable format
 *
 * @param {string} date - The date string to format
 * @returns {string} The formatted date string
 */
const formatDateLabel = (date: string): string => {
  return formatMonthDate(date);
};

/**
 * Filters initiatives by a search term, matching against key or name
 *
 * @param {Initiative[]} initiatives - The array of initiatives to filter
 * @param {string} searchTerm - The search term to match against
 * @returns {Initiative[]} Filtered array of initiatives matching the search term
 */
const filterInitiativesBySearchTerm = (initiatives: Initiative[], searchTerm: string): Initiative[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return initiatives;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();

  return initiatives.filter((initiative) => {
    const keyMatch = initiative.key && initiative.key.toLowerCase().includes(normalizedSearchTerm);
    const nameMatch = initiative.name && initiative.name.toLowerCase().includes(normalizedSearchTerm);

    return keyMatch || nameMatch;
  });
};

export { filterInitiativesBySearchTerm, formatDateLabel, formatInitiativeDate, getMetricLabel };
